import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ImageBarcodeSearch from './ImageBarcodeSearch'; // Assuming this is in the same folder or ./components/

export default function SearchScreen() {
  const [query, setQuery] = useState("");
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [customIngredient, setCustomIngredient] = useState("");
  const [useCustomIngredient, setUseCustomIngredient] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();
  const apiKey = process.env.REACT_APP_FOODGYDE_API_KEY;

  const ingredients = ["Egg", "Milk", "Nuts", "Soy", "Artificial Colors"];

  const handleIngredientCheckboxChange = (value) => {
    setSelectedIngredients((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const [certifications, setCertifications] = useState({
    CertifiedVegan: false,
    CertifiedPlantBased: false,
    CertifiedVegetarian: false,
    CertifiedKosher: false,
    CertifiedGlutenFree: false,
    CertifiedDairyFree: false,
    CertifiedLactoseFree: false,
  });

  const performGeneralSearchNavigation = (currentQuery) => {
    const allIngredients = [...selectedIngredients];
    if (useCustomIngredient && customIngredient.trim() !== "") {
      allIngredients.push(customIngredient.trim());
    }

    // Construct URL parameters primarily for bookmarking/sharing if desired.
    // ItemListScreen will primarily use the 'state' for its POST request.
    const params = new URLSearchParams();
    if (currentQuery) params.append("query", currentQuery);
    // You could add other filters to params here if you want them in the URL too

    console.log("SearchScreen: Navigating to /items with state:", {
      query: currentQuery,
      ingredients: allIngredients,
      certifications,
    });

    navigate(`/items?${params.toString()}`, {
      state: {
        query: currentQuery,
        ingredients: allIngredients,
        certifications,
      },
    });
    setIsSearching(false); // Reset loading state
  };

  const handleSearch = async () => {
    const trimmedQuery = query.trim();
    if (trimmedQuery === "") {
      return;
    }

    setIsSearching(true);

    const isPotentiallyBarcode = /^\d{8,14}$/.test(trimmedQuery);
    console.log("SearchScreen - Query:", trimmedQuery, "Is Potentially Barcode:", isPotentiallyBarcode);

    if (isPotentiallyBarcode) {
      try {
        console.log("SearchScreen - Attempting to fetch by barcode from API:", trimmedQuery);
        const response = await axios.get(`https://api.foodgyde.com/barcode/${trimmedQuery}`, {
          headers: { "x-api-key": apiKey }
        });
        console.log("SearchScreen - Response from /barcode:", response);

        if (response.data && response.data.id) {
          console.log("SearchScreen - Barcode found! Navigating to /item/", response.data.id);
          navigate(`/item/${response.data.id}`);
          setIsSearching(false);
          return;
        } else {
          console.warn("SearchScreen - Barcode not found via API (data or data.id missing), falling back to general search. Data:", response.data);
          performGeneralSearchNavigation(trimmedQuery);
        }
      } catch (error) {
        console.error("SearchScreen - Error fetching item by barcode (catch block):", error.response || error.message || error);
        console.warn("SearchScreen - Falling back to general search due to barcode API error.");
        performGeneralSearchNavigation(trimmedQuery);
      }
    } else {
      performGeneralSearchNavigation(trimmedQuery);
    }
  };

  const labelMap = {
    CertifiedVegan: "Vegan",
    CertifiedPlantBased: "Plant-Based",
    CertifiedVegetarian: "Vegetarian",
    CertifiedKosher: "Kosher",
    CertifiedGlutenFree: "Gluten-Free",
    CertifiedDairyFree: "Dairy-Free",
    CertifiedLactoseFree: "Lactose-Free",
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center">
      <header className="w-full bg-blue-600 p-4 text-white text-center text-lg font-bold">
        Food Gyde
      </header>
      <main className="flex-grow flex flex-col items-center justify-center px-4 w-full">
        <div className="w-full max-w-lg">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for items or enter a barcode"
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isSearching}
          />
          
          <div className="mt-4">
            <h3 className="text-gray-700 font-semibold mb-2">Manufacturer Certified</h3>
            <div className="flex flex-wrap gap-4">
              {Object.keys(certifications).map((cert) => (
                <label key={cert} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={certifications[cert]}
                    onChange={() =>
                      setCertifications({ ...certifications, [cert]: !certifications[cert] })
                    }
                    className="mr-2"
                    disabled={isSearching}
                  />
                  {labelMap[cert] || cert}
                </label>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <h3 className="text-gray-700 font-semibold mb-2">Exclude Ingredients</h3>
            <div className="flex flex-wrap gap-4">
              {ingredients.map((ingredient) => (
                <label key={ingredient} className="flex items-center">
                  <input
                    type="checkbox"
                    value={ingredient}
                    checked={selectedIngredients.includes(ingredient)}
                    onChange={() => handleIngredientCheckboxChange(ingredient)}
                    className="mr-2"
                    disabled={isSearching}
                  />
                  {ingredient}
                </label>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <input
                type="checkbox"
                checked={useCustomIngredient}
                onChange={() => setUseCustomIngredient(!useCustomIngredient)}
                className="mr-2"
                disabled={isSearching}
              />
              <span>Other:</span>
              {useCustomIngredient && (
                <input
                  type="text"
                  value={customIngredient}
                  onChange={(e) => setCustomIngredient(e.target.value)}
                  placeholder="Enter an ingredient..."
                  className="flex-grow p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSearching}
                />
              )}
            </div>
          </div>
          <button
            onClick={handleSearch}
            className="mt-6 w-full px-6 py-3 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700 disabled:opacity-50"
            disabled={isSearching || query.trim() === ""} // Disable if searching or query is empty
          >
            {isSearching ? "Searching..." : "Search"}
          </button>
        </div>
        <div className="w-full max-w-lg mt-8">
          <ImageBarcodeSearch /> {/* Your image search component */}
        </div>
      </main>
    </div>
  );
}